import React, { useCallback, useEffect, useState } from "react";
import { Button, List, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { updateCurrentChat } from "../../../../redux/slices/chatSlice";
import { useAppDispatch } from "../../../../redux/utils/Dispatcher";
import { formatSingleChat } from "../../../../utils";
import { customLogger } from "../../../../utils/Logger";
import ChatSidebarHeader from "../../../../components/common/ChatSidebarHeader";
import SearchInput from "../../../../components/common/UI/SearchInput";
import CustomLoader from "../../../../components/common/UI/Loader";
import ContactCard from "../../../../components/Chat/ContactCard";
import GradientButton from "../../../../components/common/UI/GradientButton";
import ENDPOINTS from "../../../../services/endpoint";
import apiInstance from "../../../../services/apiService";
import "./index.scss";

interface AddNewRecipientProps {
  handleBackClick: () => void;
  broadCastInfo: any;
}

interface Contact {
  _id: string;
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  iseFone: boolean;
  block?: any;
}

const AddNewRecipient: React.FC<AddNewRecipientProps> = ({
  handleBackClick,
  broadCastInfo,
}) => {
  const dispatch = useAppDispatch();
  const [filteredContacts, setFilteredContacts] = useState<Contact[] | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [selectedNumberToUnblock, setSelectedNumberToUnblock] =
    useState<Contact | null>(null);
  const [efoneContactsList, setEfoneContactsList] = useState<Contact[] | null>(
    null
  );

  const fetchBroadCastInfo = useCallback(async () => {
    const URL = ENDPOINTS.CONVERSATION_LIST + `?cId=${broadCastInfo.id}`;

    setLoading(true);
    try {
      const response = await apiInstance.get(URL);
      if (response.status === 200) {
        if (broadCastInfo.isArchived) {
          const conversation = formatSingleChat(
            response.data.newCnv?.docs?.[0]?.archivedList?.[0]
          );
          return conversation;
        } else {
          const conversation = formatSingleChat(
            response.data.newCnv?.docs?.[0]?.conversationList?.[0]
          );
          return conversation;
        }
      }
    } catch (err: any) {
      console.error("Error fetching conversations:", err);
      toast.error(err.message || "There was an error fetching Conversations");
    } finally {
      setLoading(false);
    }
  }, [broadCastInfo]);

  const getContactsList = async () => {
    const URL = ENDPOINTS.CONTACTS_LIST;

    setLoading(true);
    try {
      const response = await apiInstance.get(URL);
      if (response.status === 200) {
        const contacts = response.data.data;
        const efoneContacts = contacts.filter(
          (contact: Contact) => contact.iseFone
        );
        setEfoneContactsList(efoneContacts);
      }
    } catch (err: any) {
      toast.error(err.msg || "Failed to get contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleContactSelection = (contact: Contact, checked: boolean) => {
    if (contact.block) {
      setSelectedNumberToUnblock(contact);
      setShowBlockModal(true);
      return;
    }

    setSelectedContacts((prev: Contact[]) => {
      const exists = prev.some((c) => c._id === contact._id);
      if (checked && !exists) {
        return [...prev, contact];
      } else if (!checked && exists) {
        return prev.filter((c) => c._id !== contact._id);
      }
      return prev;
    });
  };

  const handleUnblockContact = async () => {
    setLoading(true);
    try {
      const response = await apiInstance.delete(
        `${ENDPOINTS.BLOCK_NUMBER}/${selectedNumberToUnblock?.block?._id}`
      );

      if (response.status === 200) {
        toast.success("Phone Number updated successfully!");
        setShowBlockModal(false);

        const updatedNumber: Contact = {
          _id: selectedNumberToUnblock!._id,
          fullName: selectedNumberToUnblock!.fullName,
          phoneNumber: selectedNumberToUnblock!.phoneNumber,
          emailAddress: selectedNumberToUnblock!.emailAddress,
          iseFone: selectedNumberToUnblock!.iseFone,
        };

        handleContactSelection(updatedNumber, true);
      }
    } catch (error) {
      customLogger(error);
      toast.error("There was an error updating");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedContacts.length) {
      toast.error("Please select at least one contact");
      return;
    }

    const URL = ENDPOINTS.UPDATE_BROADCAST;

    const recipientsNumbers = selectedContacts.map(
      (contact) => contact.phoneNumber
    );

    setAddLoading(true);
    try {
      const response = await apiInstance.post(URL, {
        chatId: broadCastInfo.id,
        newGroupMembers: recipientsNumbers,
      });

      if (response.status === 200) {
        const updatedRecipients = await fetchBroadCastInfo();
        dispatch(
          updateCurrentChat({
            broadCastMembers: updatedRecipients?.broadCastMembers,
          })
        );
        getContactsList();
        toast.success(response.data.message || "Added successfully");
      }
    } catch (err: any) {
      console.error("Error removing member:", err);
      toast.error(err.message || "There was an error. Please try again");
    } finally {
      setAddLoading(false);
    }
  };

  useEffect(() => {
    getContactsList();
  }, []);

  useEffect(() => {
    if (!efoneContactsList) return;

    const recipientNumbers = broadCastInfo?.broadCastMembers?.map(
      (member: Contact) => member.phoneNumber
    );

    const filtered = efoneContactsList.filter(
      (contact) => !recipientNumbers?.includes(contact.phoneNumber)
    );

    setFilteredContacts(filtered);
    setLoading(false);
  }, [efoneContactsList, broadCastInfo]);

  useEffect(() => {
    if (!filteredContacts) return;

    const query = searchQuery?.toLowerCase();
    const filtered = filteredContacts?.filter(
      (contact: Contact) =>
        contact.fullName?.toLowerCase().includes(query) ||
        contact.phoneNumber.includes(query)
    );
    setFilteredContacts(filtered);
  }, [searchQuery]);

  return (
    <>
      <div className="add-new-recipient">
        <ChatSidebarHeader
          title="Add New Recipient"
          handleBackClick={handleBackClick}
        />

        <div className="hero-section">
          <div className="search-input-container">
            <SearchInput
              disabled={loading}
              prefix={<SearchOutlined />}
              placeholder="Search by name or phone number..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <div className="efone-contacts-list">
            <List
              dataSource={filteredContacts || []}
              loading={{
                spinning: loading,
                indicator: <CustomLoader />,
              }}
              renderItem={(contact: Contact) => {
                const isChecked = selectedContacts.some(
                  (selected) => selected.phoneNumber === contact.phoneNumber
                );
                return (
                  <ContactCard
                    key={contact._id}
                    userName={contact.fullName}
                    phoneNumber={contact.phoneNumber}
                    isEfone={contact.iseFone}
                    checkbox
                    checked={isChecked}
                    onChecked={(checked) =>
                      handleContactSelection(contact, checked)
                    }
                  />
                );
              }}
            />
          </div>

          <div className="btn-container">
            <GradientButton
              loading={addLoading}
              onClick={handleAddMembers}
              className="next-btn"
            >
              Add
            </GradientButton>
          </div>
        </div>
      </div>
      <Modal
        centered
        title={"Confirm Unblock"}
        open={showBlockModal}
        onCancel={() => {
          setShowBlockModal(false);
          setSelectedNumberToUnblock(null);
        }}
        footer={
          <>
            <Button
              className="modal-cancel-btn"
              type="text"
              onClick={() => setShowBlockModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="modal-ok-btn danger"
              onClick={handleUnblockContact}
              loading={loading}
            >
              Unblock
            </Button>
          </>
        }
        className="custom-modal"
      >
        <p className="text-primary">
          Are you sure you want to Unblock this chat?
        </p>
      </Modal>
    </>
  );
};

export default AddNewRecipient;
